import React, { useState } from 'react';
import { Form, Input, Select, Checkbox, InputNumber, Card, Space, Button, Switch } from 'antd';

import { ApiError, DeviceTypeSettings, Rule, StbGroup, StbGroupExtended } from '@services/types';

import styles from './stbGroup.module.scss';
import api from '@services/api';
import { DeploymentMode, FirmwareType, TagsResponse } from '@openapi';
import { toast } from 'react-toastify';
import { useHistory, useParams } from 'react-router-dom';
import { STBGroupContext } from '.';
import { AppContext } from '@layouts/AppRouter';
import { selectSearchFilter } from '@services/utils';
import { Navigation } from '@constants/Navigation';

const { Option } = Select;

const arr: string[] = [];

const EditStbGroupModal: React.FC = () => {
  const [formValues, setFormValues] = React.useState<Partial<StbGroupExtended>>();
  const {
    deviceTypes,
    superGroups,
    hints,
    stbGroups,
    setStbGroups,
    setSelectedSuper,
    setSelectedDevice,
    selectedDevice,
  } = React.useContext(STBGroupContext);
  const { settings } = React.useContext(AppContext);
  const [stbGroupToUpdate, setStbGroupToUpdate] = React.useState<StbGroupExtended>();
  const [stbTypes, setStbTypes] = React.useState<StbGroupExtended['superGroupId']>();
  const [deviceType, setDeviceType] = React.useState<StbGroupExtended['deviceTypeId']>();
  const [deviceTypeSettings, setDeviceTypeSettings] = React.useState<DeviceTypeSettings>();
  const [stbRules, setStbRules] = React.useState<Rule[]>([]);
  const [tagsDevice, setTagsDevice] = React.useState<Set<TagsResponse>>(new Set<TagsResponse>());
  const [tagLocked, setTagLocked] = React.useState(false);
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const [form] = Form.useForm<Partial<StbGroupExtended>>();
  const bpArray = new Array(100).fill(1).map((a, i) => ('0' + i).slice(-2));

  const SwitchText = () => {
    return (
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <span style={{ marginRight: 10 }}>Locking</span>
        <Switch
          checked={tagLocked}
          onChange={() => {
            setTagLocked(!tagLocked);
          }}
        />
        <span style={{ marginLeft: 10 }}>Unlocking</span>
      </div>
    );
  };

  React.useEffect(() => {
    if (id) {
      setStbGroupToUpdate(stbGroups.find((stb) => stb.id === id));
    }
    if (deviceType || stbGroupToUpdate?.deviceTypeId) {
      api.administration
        .getDeviceTypeSettings(deviceType || stbGroupToUpdate?.deviceTypeId || '')
        .then((data) => setDeviceTypeSettings(data));
    }
  }, [id, stbGroups, deviceType, stbGroupToUpdate?.deviceTypeId]);

  React.useEffect(() => {
    if (stbGroupToUpdate) {
      arr.length = 0;
      arr.push(stbGroupToUpdate?.deviceTypeId || deviceType!);
      form.setFieldsValue(stbGroupToUpdate);
      setFormValues(stbGroupToUpdate);
    } else if (deviceType) {
      arr.length = 0;
      arr.push(deviceType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form, stbGroupToUpdate, stbGroupToUpdate?.deviceTypeId, deviceType]);

  React.useEffect(() => {
    if ((deviceType || stbGroupToUpdate?.deviceTypeId) && (stbGroupToUpdate?.superGroupId || stbTypes)) {
      console.log('Log id : ', id);
      const paramDeviceType = deviceType || stbGroupToUpdate?.deviceTypeId;
      Promise.all([
        api.rules.getRules({
          filterDeviceTypeIds: arr,
          filterSuperGroupId: stbGroupToUpdate?.superGroupId || stbTypes,
        }),
        api.tags.getGroupTags({ groupId: id, deviceTypeId: paramDeviceType ? paramDeviceType : '' }),
      ]).then(([data, groupTags]) => {
        setStbRules(data.elements);
        setTagsDevice(groupTags);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stbGroupToUpdate?.deviceTypeId, stbGroupToUpdate?.superGroupId, deviceType, stbTypes]);

  React.useEffect(() => {
    if (formValues?.enableQuota) {
      const newSettings: Partial<StbGroupExtended> = {};
      if (!formValues?.quotaBypassFwVersion) {
        newSettings.quotaBypassFwVersion = '0.0.0';
      }
      if (!formValues?.quotaBypassLoaderVersion) {
        newSettings.quotaBypassLoaderVersion = '0.0.0';
      }
      if (!formValues?.quotaBypassUiVersion) {
        newSettings.quotaBypassUiVersion = '0.0.0';
      }
      const newValues = { ...(formValues || {}), ...newSettings };
      setFormValues(newValues);
      form.setFieldsValue(newValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.enableQuota]);

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (stbGroupToUpdate) {
        await api.stbGroups.updateStbGroup({
          deviceTypeId: '',
          superGroupId: '',
          name: '',
          description: '',
          upgradeRuleId: '',
          deploymentMode: DeploymentMode.User,
          enableQuota: false,
          bpProfile: 0,
          quota: 0,
          quotaFirmwareType: FirmwareType.Ui,
          quotaDailyIncrement: 0,
          quotaBypassLoaderVersion: '',
          quotaBypassFwVersion: '',
          quotaBypassUiVersion: '',
          ipGroup: false,
          ipMask: '',
          isIpGroup: false,
          ...(stbGroupToUpdate as Partial<StbGroup>),
          ...values,
          id: stbGroupToUpdate.id,
        });
        toast.success('Group was updated successfully');
      } else {
        await api.stbGroups.createStbGroup(
          {
            deviceTypeId: '',
            superGroupId: '',
            name: '',
            description: '',
            upgradeRuleId: '',
            deploymentMode: DeploymentMode.User,
            enableQuota: false,
            bpProfile: 0,
            quota: 0,
            quotaFirmwareType: FirmwareType.Ui,
            quotaDailyIncrement: 0,
            quotaBypassLoaderVersion: '',
            quotaBypassFwVersion: '',
            quotaBypassUiVersion: '',
            ipGroup: false,
            ipMask: '',
            isIpGroup: false,
            ...values,
          },
          form.getFieldValue('makeDefault'),
        );
        toast.success('Group was created successfully');
      }
      const stbGroups = await api.stbGroups.getStbGroups({
        filterDeviceTypeId: stbGroupToUpdate ? selectedDevice : deviceType,
      });
      setSelectedDevice(stbGroupToUpdate ? selectedDevice : deviceType);
      setSelectedSuper(undefined);
      setStbGroups(
        stbGroups.elements.map((group) => ({
          ...group,
          deviceTypeName: deviceTypes.find((deviceType) => deviceType.id === group.deviceTypeId)?.deviceName,
          superGroupName: superGroups.find((superGroup) => superGroup.id === group.deviceTypeId)?.name,
        })),
      );
      form.resetFields();
      onClose();
    } catch (response) {
      if (response instanceof ApiError) {
        if (response.error.details !== null) {
          toast.error(
            `${response.error.details || Object.values([response.error.propertyErrors]).map((item) => item)}`,
          );
        } else {
          toast.error('Something went wrong');
        }
      } else {
        toast.error('Something went wrong');
      }
      console.log('CreateDeviceTypeModal ~ handleSubmit ~ error', response);
    }
  };

  const onClose = () => {
    history.push(Navigation.STBGroup);
  };

  const mappedArray = Array.from(tagsDevice || []).filter((value, index) => {
    return tagLocked ? value.command !== 'locking' : value.command !== 'unlocking';
  });
  return (
    <Card
      title={stbGroupToUpdate ? 'Modify Group' : 'Add new Group'}
      extra={
        <Space>
          <Button type="default" onClick={onClose}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            {stbGroupToUpdate ? 'Update' : 'Create'}
          </Button>
        </Space>
      }
    >
      <Form
        form={form}
        layout="horizontal"
        name="form_in_modal"
        className={styles.form}
        onValuesChange={(_, values) => setFormValues(values)}
      >
        <Form.Item
          name="deviceTypeId"
          label="Device Type"
          rules={[
            {
              required: true,
              message: 'Please input the Device Type',
            },
          ]}
        >
          <Select
            onChange={(value: StbGroupExtended['deviceTypeId']) => {
              setDeviceType(value);
            }}
            disabled={!!stbGroupToUpdate}
            allowClear
            showSearch
            defaultValue={deviceType}
            filterOption={selectSearchFilter}
          >
            {deviceTypes.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.deviceName}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="superGroupId"
          label="Scope"
          rules={[
            {
              required: true,
              message: 'Please input the Scope',
            },
          ]}
        >
          <Select
            onChange={(value: StbGroupExtended['superGroupId']) => {
              setStbTypes(value);
            }}
            allowClear
            disabled={!!stbGroupToUpdate}
            showSearch
            filterOption={selectSearchFilter}
          >
            {superGroups.map((item) => (
              <Option key={item.id} value={item.id}>
                {item.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="name"
          label="Name"
          rules={[
            {
              required: true,
              message: 'Please input the group name!',
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="description"
          label="Description"
          rules={[
            {
              required: true,
              message: 'Please input the group description!',
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="upgradeRuleId"
          label="Update rule"
          rules={[
            {
              required: true,
              message: 'Please input the Update rule',
            },
          ]}
        >
          <Select allowClear showSearch filterOption={selectSearchFilter}>
            {(stbGroupToUpdate?.deviceTypeId || deviceType) &&
              (stbGroupToUpdate?.superGroupId || stbTypes) &&
              stbRules.map((item) => (
                <Option key={item.id} value={item.id}>
                  {item.name}
                </Option>
              ))}
          </Select>
        </Form.Item>
        {hints?.enableDeploymentModes && deviceTypeSettings?.enableDeploymentMode ? (
          <Form.Item name="deploymentMode" label="Deployment Mode">
            <Select allowClear showSearch filterOption={selectSearchFilter}>
              <Option value={DeploymentMode.Mandatory}>{DeploymentMode.Mandatory}</Option>
              <Option value={DeploymentMode.Recommended}>{DeploymentMode.Recommended}</Option>
              <Option value={DeploymentMode.User}>{DeploymentMode.User}</Option>
            </Select>
          </Form.Item>
        ) : (
          <div />
        )}
        {settings?.enableTags && stbGroupToUpdate && (
          <Form.Item name="isLocked">
            <SwitchText />
          </Form.Item>
        )}
        {stbGroupToUpdate && settings?.enableTags && <div />}
        {settings?.enableTags && stbGroupToUpdate && (
          <div>
            <Form.Item name="tagIds" label="Tags">
              <Select style={{ width: '50%' }} mode="multiple" allowClear showSearch filterOption={selectSearchFilter}>
                {mappedArray.map((item) => (
                  <Option key={item?.id}>{item?.command}</Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        )}
        {stbGroupToUpdate && settings?.enableTags && <div />}
        {!stbGroupToUpdate ? (
          <Form.Item valuePropName="checked" name="makeDefault">
            <Checkbox>Default group</Checkbox>
          </Form.Item>
        ) : (
          <div />
        )}
        <div />
        <div />
        <div />
        <Form.Item noStyle>
          {hints?.enableQuota && deviceTypeSettings?.enableQuota ? (
            <Form.Item name="enableQuota" valuePropName="checked">
              <Checkbox>Enable Quota</Checkbox>
            </Form.Item>
          ) : (
            <div />
          )}
        </Form.Item>

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) => {
            return prevValues.enableQuota !== currentValues.enableQuota;
          }}
        >
          {hints?.enableQuota && deviceTypeSettings?.enableQuota
            ? ({ getFieldValue }) =>
                getFieldValue('enableQuota') && (
                  <Form.Item noStyle>
                    <div />
                    <Form.Item label="Overall Quota" name="quota">
                      <Input />
                    </Form.Item>
                    <Form.Item label="Quota Firmware Type" name="quotaFirmwareType">
                      <Select>
                        <Option value="LOADER">LOADER</Option>
                        <Option value="FW">FW</Option>
                        <Option value="UI">UI</Option>
                      </Select>
                    </Form.Item>
                    <Form.Item label="Daily increment" name="quotaDailyIncrement">
                      <InputNumber />
                    </Form.Item>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: 'Please set the Version',
                        },
                      ]}
                      label="LOADER Bypass Version"
                      name="quotaBypassLoaderVersion"
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: 'Please set the Version',
                        },
                      ]}
                      label="FW Bypass Version"
                      name="quotaBypassFwVersion"
                    >
                      <Input />
                    </Form.Item>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: 'Please set the Version',
                        },
                      ]}
                      label="UI Bypass Version"
                      name="quotaBypassUiVersion"
                    >
                      <Input />
                    </Form.Item>
                  </Form.Item>
                )
            : null}
        </Form.Item>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'max-content 1fr',
            gridGap: '20px',
            gridColumn: '1 / -1',
          }}
        >
          {settings?.enableIpGroup && (
            <Form.Item name="isIpGroup" valuePropName="checked">
              <Checkbox>IP Group</Checkbox>
            </Form.Item>
          )}
          <Form.Item
            noStyle
            shouldUpdate={(prevValues: StbGroup, currentValues: StbGroup) => {
              return prevValues.isIpGroup !== currentValues.isIpGroup;
            }}
          >
            {settings?.enableIpGroup
              ? ({ getFieldValue }) =>
                  getFieldValue('isIpGroup') && (
                    <Form.Item
                      label="IP Mask"
                      name="ipMask"
                      rules={[
                        {
                          required: true,
                          message: 'Please input IP',
                        },
                      ]}
                    >
                      <Input />
                    </Form.Item>
                  )
              : null}
          </Form.Item>
        </div>
        {deviceTypeSettings?.addBPProfile && (
          <div>
            <Form.Item name="bpProfile" label="BP Profile">
              <Select style={{ width: '200px' }} allowClear showSearch filterOption={selectSearchFilter}>
                {bpArray.map((item, index) => (
                  <Option key={item} value={index}>
                    {item}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </div>
        )}
      </Form>
    </Card>
  );
};

export default React.memo(EditStbGroupModal);
